/**
 * Rendering utilities for visualization
 * Canvas functions for static layers, SVG functions for interactive layers
 */

import * as d3 from 'd3';
import type { Polygon } from 'src/types';

// ---------------------------------------------------------------------------
// Canvas renderers (static layers: contours, polygons, MPLEs)
// ---------------------------------------------------------------------------

/**
 * Apply canvas masking that replicates the SVG mask/clipPath behavior:
 * 1. Clip to Attika + BoundingBox polygons
 * 2. Draw content (e.g. contours) inside the clip
 * 3. Erase Struktur polygon areas via destination-out compositing
 */
/**
 * Compute signed area of a polygon (shoelace formula).
 * Positive = CCW, negative = CW.
 */
function signedArea(coords: [number, number][]): number {
  let area = 0;
  for (let i = 0; i < coords.length; i++) {
    const j = (i + 1) % coords.length;
    area += coords[i][0] * coords[j][1];
    area -= coords[j][0] * coords[i][1];
  }
  return area / 2;
}

/**
 * Ensure polygon coordinates wind CCW (positive signed area).
 * Returns coords as-is if already CCW, reversed copy if CW.
 */
function ensureCCW(coords: [number, number][]): [number, number][] {
  return signedArea(coords) >= 0 ? coords : [...coords].reverse();
}

export function applyCanvasMasking(
  ctx: CanvasRenderingContext2D,
  polygons: Polygon[],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  drawContent: () => void
): void {
  // Lines never participate in masking — they're open polylines, not regions.
  const closedPolys = polygons.filter((p) => p.geometry_type !== 'line');
  const attikaPolys = closedPolys.filter((p) => p.description === 'Attika');
  const boundingBoxPolys = closedPolys.filter((p) => p.description === 'BoundingBox');
  const strukturPolys = closedPolys.filter((p) => p.description === 'Struktur');

  const hasClipPolygons = attikaPolys.length > 0 || boundingBoxPolys.length > 0;

  ctx.save();

  if (hasClipPolygons) {
    // Step 1: Build clipping region from Attika + BoundingBox.
    // All sub-paths must wind the same direction (CCW) so the nonzero
    // fill rule treats their union as the clip region.
    ctx.beginPath();
    [...attikaPolys, ...boundingBoxPolys].forEach((polygon) => {
      const coords = ensureCCW(polygon.coordinates);
      if (coords.length === 0) return;
      ctx.moveTo(xScale(coords[0][0]), yScale(coords[0][1]));
      for (let i = 1; i < coords.length; i++) {
        ctx.lineTo(xScale(coords[i][0]), yScale(coords[i][1]));
      }
      ctx.closePath();
    });
    ctx.clip();
  }

  // Step 2: Draw content — clipped if polygons exist, full domain otherwise
  drawContent();

  if (hasClipPolygons) {
    // Step 3: Erase Struktur areas
    ctx.globalCompositeOperation = 'destination-out';
    strukturPolys.forEach((polygon) => {
      const coords = polygon.coordinates;
      if (coords.length === 0) return;
      ctx.beginPath();
      ctx.moveTo(xScale(coords[0][0]), yScale(coords[0][1]));
      for (let i = 1; i < coords.length; i++) {
        ctx.lineTo(xScale(coords[i][0]), yScale(coords[i][1]));
      }
      ctx.closePath();
      ctx.fill();
    });
  }

  ctx.restore();
}

/**
 * Compute contour geometries from grid data using d3.contours (marching squares)
 * Separated from rendering to allow independent timing and caching.
 */
export function computeContours(
  gridValues: number[],
  gridNx: number,
  gridNy: number,
  zExtent: [number, number],
  contourStep: number = 0.5
): d3.ContourMultiPolygon[] {
  const thresholds = d3.range(zExtent[0], zExtent[1] + contourStep, contourStep);
  return d3.contours().size([gridNx, gridNy]).thresholds(thresholds)(gridValues);
}

/**
 * Render pre-computed contour geometries onto a canvas context.
 *
 * gridDomain is the original world-coordinate domain that the pipeline used
 * to build the grid. This may differ from xScale.domain() / yScale.domain()
 * because getScales() adjusts the scale domains for aspect-ratio centering.
 * Using the adjusted domain here would stretch/shift the contour field
 * relative to the SVG point layer.
 */
export function renderContoursCanvas(
  ctx: CanvasRenderingContext2D,
  contours: d3.ContourMultiPolygon[],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  gridNx: number,
  gridNy: number,
  colorScale: d3.ScaleSequential<string>,
  gridDomain?: { x: [number, number]; y: [number, number] }
): void {
  // Use the original grid domain if provided, otherwise fall back to scale domain
  const xExtent = gridDomain?.x ?? (xScale.domain() as [number, number]);
  const yExtent = gridDomain?.y ?? (yScale.domain() as [number, number]);
  const xStep = (xExtent[1] - xExtent[0]) / (gridNx - 1);
  const yStep = (yExtent[1] - yExtent[0]) / (gridNy - 1);

  const pathRenderer = d3.geoPath(
    d3.geoTransform({
      point: function (x, y) {
        this.stream.point(xScale(xExtent[0] + x * xStep), yScale(yExtent[0] + y * yStep));
      },
    }),
    ctx
  );

  // Fill background with min color so uncovered areas don't show as white
  ctx.fillStyle = colorScale(contours[0]?.value ?? 0);
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  contours.forEach((contour) => {
    ctx.beginPath();
    pathRenderer(contour);
    ctx.fillStyle = colorScale(contour.value);
    ctx.fill();
  });
}

/**
 * Draw polygon boundaries on canvas (Struktur = thin, Attika = thick)
 */
export function drawPolygonsCanvas(
  ctx: CanvasRenderingContext2D,
  polygons: Polygon[],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>
): void {
  try {
    const linePolys = polygons.filter((p) => p.geometry_type === 'line');
    const closedPolys = polygons.filter((p) => p.geometry_type !== 'line');

    const strukturPolys = closedPolys.filter((p) => p.description === 'Struktur');
    const attikaPolys = closedPolys.filter((p) => p.description === 'Attika');

    strukturPolys.forEach((polygon) => {
      drawPolygonPath(ctx, polygon.coordinates, xScale, yScale);
      ctx.strokeStyle = '#484444';
      ctx.lineWidth = 1;
      ctx.stroke();
    });

    attikaPolys.forEach((polygon) => {
      drawPolygonPath(ctx, polygon.coordinates, xScale, yScale);
      ctx.strokeStyle = '#343434';
      ctx.lineWidth = 4;
      ctx.stroke();
    });

    linePolys.forEach((line) => {
      drawLinePath(ctx, line.coordinates, xScale, yScale);
      ctx.strokeStyle = '#666666';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    });
  } catch (error) {
    console.error('Error drawing polygons on canvas:', error);
  }
}

function drawPolygonPath(
  ctx: CanvasRenderingContext2D,
  coords: [number, number][],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>
): void {
  if (coords.length === 0) return;
  ctx.beginPath();
  ctx.moveTo(xScale(coords[0][0]), yScale(coords[0][1]));
  for (let i = 1; i < coords.length; i++) {
    ctx.lineTo(xScale(coords[i][0]), yScale(coords[i][1]));
  }
  ctx.closePath();
}

function drawLinePath(
  ctx: CanvasRenderingContext2D,
  coords: [number, number][],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>
): void {
  if (coords.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(xScale(coords[0][0]), yScale(coords[0][1]));
  for (let i = 1; i < coords.length; i++) {
    ctx.lineTo(xScale(coords[i][0]), yScale(coords[i][1]));
  }
  // No closePath — open polyline.
}

/**
 * Draw MPLE (Multi-Point Line Electrodes) as connected polylines on canvas
 */
export function drawMPLEsCanvas(
  ctx: CanvasRenderingContext2D,
  points: any[],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>
): void {
  try {
    // Group into polylines. mple is only unique WITHIN a cabinet, so on
    // multi-cabinet installations the same mple appears once per cabinet —
    // include schaltschranknummer in the key so each cabinet's mple is its own
    // polyline instead of one line zig-zagging between cabinets.
    const lineKey = (d: any) => {
      const id = d.mple ?? d.cable ?? 'unknown';
      return d.schaltschranknummer != null ? `${d.schaltschranknummer}:${id}` : `${id}`;
    };
    const groupedPoints = d3.group(points, lineKey);

    const sortedGroups = Array.from(groupedPoints, ([mple, pts]) => {
      pts.sort((a: any, b: any) => {
        if (a.abgriff !== undefined && b.abgriff !== undefined) return a.abgriff - b.abgriff;
        if (a.electrode !== undefined && b.electrode !== undefined)
          return a.electrode - b.electrode;
        return 0;
      });
      return [mple, pts] as const;
    });

    const mpleColorScale = d3
      .scaleOrdinal()
      .domain([...groupedPoints.keys()] as any[])
      .range(d3.schemeCategory10);

    sortedGroups.forEach(([mple, pts]) => {
      if (pts.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(xScale(pts[0].x), yScale(pts[0].y));
      for (let i = 1; i < pts.length; i++) {
        ctx.lineTo(xScale(pts[i].x), yScale(pts[i].y));
      }
      ctx.strokeStyle = mpleColorScale(mple) as string;
      ctx.lineWidth = 2;
      ctx.stroke();
    });
  } catch (error) {
    console.error('Error drawing MPLEs on canvas:', error);
  }
}

// ---------------------------------------------------------------------------
// SVG renderers (interactive layers: points with tooltips)
// ---------------------------------------------------------------------------

/**
 * Draw measurement points as circles with click-to-select.
 * Alerts are highlighted with a thick white stroke. Operational staleness of a
 * cabinet is communicated by the CabinetStatusBar chips and by cabinet alert
 * boxes on the map, so individual carried-forward points render like any other.
 */
export function drawPoints(
  svg: d3.Selection<any, unknown, null, undefined>,
  points: any[],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  colorScale: d3.ScaleSequential<string>,
  onClick: (point: any) => void,
  radius: number = 3.5
): void {
  try {
    svg
      .selectAll('.point')
      .data(points)
      .enter()
      .append('circle')
      .attr('class', 'point')
      .attr('cx', (d: any) => xScale(d.x))
      .attr('cy', (d: any) => yScale(d.y))
      .attr('r', radius)
      .attr('fill', (d: any) => colorScale(d._value))
      .attr('opacity', (d: any) => (d._alert ? 1 : 0.7))
      .attr('stroke', (d: any) => (d._alert ? 'white' : 'black'))
      .attr('stroke-width', (d: any) => (d._alert ? 2 : 1))
      .style('cursor', 'pointer')
      .on('click', (_event: any, d: any) => onClick(d));
  } catch (error) {
    console.error('Error drawing points:', error);
  }
}

/**
 * A cabinet (Schaltschrank) region to annotate on the map because an active
 * alert references it. Coordinates are in world/installation space.
 */
export interface CabinetBox {
  schaltschranknummer: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  color: string;
  title?: string;
}

/**
 * Draw labeled boxes around cabinet areas that have an active alert. Each box is
 * a rounded, dashed rectangle (world→screen via the scales) in the alert color,
 * with a faint alert-colored fill and a "⚠ Schaltschrank N" pill at the top-left
 * so it reads as an alert region rather than a neutral annotation.
 *
 * Drawn on top of the point layer (so the label is never hidden behind points),
 * but with pointer-events disabled and no opaque fill, so the points underneath
 * stay visible and clickable.
 */
export function drawCabinetBoxes(
  group: d3.Selection<any, unknown, null, undefined>,
  boxes: CabinetBox[],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  label: (nr: string) => string
): void {
  try {
    boxes.forEach((box) => {
      // yScale is typically inverted (screen y grows downward), so the world
      // min/max don't map to a fixed screen corner — normalize after mapping.
      const sx0 = xScale(box.x0);
      const sx1 = xScale(box.x1);
      const sy0 = yScale(box.y0);
      const sy1 = yScale(box.y1);
      const left = Math.min(sx0, sx1);
      const top = Math.min(sy0, sy1);
      const width = Math.abs(sx1 - sx0);
      const height = Math.abs(sy1 - sy0);

      const g = group.append('g').attr('class', 'cabinet-box');
      if (box.title) g.append('title').text(box.title);
      g.append('rect')
        .attr('x', left)
        .attr('y', top)
        .attr('width', width)
        .attr('height', height)
        .attr('rx', 4)
        // Faint tint in the alert color — enough to read the region as flagged
        // without obscuring the sensors underneath.
        .attr('fill', box.color)
        .attr('fill-opacity', 0.08)
        .attr('stroke', box.color)
        .attr('stroke-width', 2.5)
        .attr('stroke-dasharray', '6,4');

      // Alert pill at the top-left corner: ⚠ + "Schaltschrank N", filled in the
      // alert color so the association with an alert is unmistakable.
      const text = `⚠ ${label(box.schaltschranknummer)}`;
      const padX = 6;
      const padY = 3;
      const fontSize = 12;
      const pillH = fontSize + padY * 2;
      const charW = fontSize * 0.62; // rough advance width for the pill sizing
      const pillW = text.length * charW + padX * 2;
      const pill = g.append('g').attr('transform', `translate(${left}, ${top - pillH - 2})`);
      pill
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', pillW)
        .attr('height', pillH)
        .attr('rx', pillH / 2)
        .attr('fill', box.color);
      pill
        .append('text')
        .attr('x', padX)
        .attr('y', pillH / 2)
        .attr('dominant-baseline', 'central')
        .attr('fill', 'white')
        .attr('font-size', fontSize)
        .attr('font-weight', 600)
        .text(text);
    });
  } catch (error) {
    console.error('Error drawing cabinet boxes:', error);
  }
}
